import { useContext, useEffect, useLayoutEffect } from "react";

import * as Sentry from "@sentry/react";
import {
  getUserIdFromRefreshToken,
  RemoteConfigClient,
  RemoteConfigProvider,
  type RemoteConfigReactClient,
  Theme,
} from "@shapr3d/frontend-lib";
import { QueryClientProvider } from "@tanstack/react-query";
import queryString from "query-string";
import { CookiesProvider } from "react-cookie";
import { Helmet } from "react-helmet";
import { Toaster } from "react-hot-toast";
import { IntlProvider } from "react-intl";
import { Outlet, useLocation } from "react-router-dom";

import { remoteConfigEnvironmentId } from "../../../config";
import { AppsFlyerContext } from "../../../contexts/AppsFlyer";
import ConfigProvider from "../../../contexts/ConfigProvider";
import StoreProvider from "../../../contexts/StoreProvider";
import useUrlHelper from "../../../hooks/urlHelper";
import { PaddleProvider } from "../../../hooks/usePaddle";
import useTheme from "../../../hooks/useTheme";
import { useTracker } from "../../../hooks/useTracker";
import { trackingAdapter } from "../../../lib/TrackingAdapter";
import de from "../../../locales/lang/de.json";
import es from "../../../locales/lang/es.json";
import fr from "../../../locales/lang/fr.json";
import it from "../../../locales/lang/it.json";
import ja from "../../../locales/lang/ja.json";
import ko from "../../../locales/lang/ko.json";
import pt_BR from "../../../locales/lang/pt_BR.json";
import ru from "../../../locales/lang/ru.json";
import zh from "../../../locales/lang/zh-Hans.json";
import zh_Hant from "../../../locales/lang/zh-Hant.json";
import { queryClient } from "../../../stores/queryClient";
import type { SupportedLocale } from "../../../types";
import { SearchParam } from "../../../types";
import ErrorPage from "../../ErrorPage/ErrorPage";
import ReCAPTCHAProvider from "../../ReCAPTCHA/ReCAPTCHAProvider";
import { IdentifyLoggedInUser } from "./IdentifyLoggedInUser";
import { TrackPageView } from "./TrackPageView";

const messages: { [local in SupportedLocale]: Record<string, string> } = {
  en: {},
  de,
  es,
  fr,
  ja,
  it,
  ko,
  "pt-BR": pt_BR,
  ru,
  zh,
  "zh-hant": zh_Hant,
  "zh-hans": zh,
};

export const remoteConfigClient = new RemoteConfigClient({
  environmentID: remoteConfigEnvironmentId,
  identityID: trackingAdapter.getTrackingID(),
});

export let remoteConfigReactClient: RemoteConfigReactClient;

export const AppProviders = () => {
  const tracker = useTracker();
  const appsFlyer = useContext(AppsFlyerContext);
  const location = useLocation();
  const urlHelper = useUrlHelper();

  const [, setTheme] = useTheme();
  const originalQuery = queryString.parse(location.search);
  const inapp = originalQuery[SearchParam.InApp];
  const theme = originalQuery[SearchParam.Theme];
  useLayoutEffect(() => {
    if (inapp === "true") {
      setTheme(theme === "dark" ? Theme.Dark : Theme.Light);
    }
  }, [inapp, theme, setTheme]);

  useEffect(() => {
    tracker.setUserData({
      userID: getUserIdFromRefreshToken(),
    });
  }, [tracker]);

  const locale = urlHelper.getLocale();

  appsFlyer.setSearch(location.search);

  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorPage />}
      beforeCapture={(scope) => {
        scope.setTag("location", "app");
      }}
    >
      <Helmet>
        <title>Shapr3D</title>
        <meta name="description" content="" />
      </Helmet>
      <RemoteConfigProvider
        remoteConfigClient={remoteConfigClient}
        ref={(client: RemoteConfigReactClient) => {
          remoteConfigReactClient = client;
        }}
      >
        <TrackPageView />
        <ReCAPTCHAProvider>
          <CookiesProvider>
            <ConfigProvider>
              <StoreProvider>
                <QueryClientProvider client={queryClient}>
                  <IdentifyLoggedInUser />
                  <IntlProvider locale={locale} messages={messages[locale]}>
                    <PaddleProvider>
                      <Toaster
                        containerStyle={{ top: "88px", position: "relative", left: 0, right: 0 }}
                        position="top-center"
                        toastOptions={{
                          icon: null,
                          style: {
                            transformOrigin: "50% 300%",
                            color: "var(--text-primary-label)",
                            background: "var(--snackbar-background)",
                            borderRadius: "var(--radius)",
                            boxShadow: "none",
                            fontSize: "12px",
                          },
                          success: {
                            style: {
                              background:
                                "linear-gradient(var(--page-primary-background), var(--page-primary-background)), var(--success-background)",
                              backgroundBlendMode: "darken",
                            },
                          },
                          error: {
                            style: {
                              background:
                                "linear-gradient(var(--page-primary-background), var(--page-primary-background)), var(--critical-background)",
                              backgroundBlendMode: "darken",
                            },
                          },
                        }}
                      />
                      <Outlet />
                    </PaddleProvider>
                  </IntlProvider>
                </QueryClientProvider>
              </StoreProvider>
            </ConfigProvider>
          </CookiesProvider>
        </ReCAPTCHAProvider>
      </RemoteConfigProvider>
    </Sentry.ErrorBoundary>
  );
};
